<i18n>
{
  "de": {
    "colors": "Farben"
  },
  "en": {
    "colors": "Colors"
  }
}
</i18n>

<template>
  <div
    v-if="showColorMenu"
    class="color-menu-button-wrapper"
    :class="{ 'color-menu-button-wrapper--open': colorMenuOpen }"
  >
    <!-- Color Menu Button -->
    <button
      class="color-menu-button"
      :class="{
        'color-menu-button--mobile': isMobileView,
        'color-menu-button--active': colorMenuOpen
      }"
      :title="$t('colors')"
      @click="toggleColorMenu"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="color-menu-button__icon"
      >
        <path d="M10 2v2" />
        <path d="M14 2v4" />
        <path d="M17 2a1 1 0 0 1 1 1v9H6V3a1 1 0 0 1 1-1z" />
        <path d="M6 12a1 1 0 0 0-1 1v1a2 2 0 0 0 2 2h2a1 1 0 0 1 1 1v2.9a2 2 0 1 0 4 0V17a1 1 0 0 1 1-1h2a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1" />
      </svg>
    </button>

    <!-- Color Menu Panel -->
    <transition name="slide-up">
      <div
        v-if="colorMenuOpen"
        class="color-menu-button__panel"
        :class="{ 'color-menu-button__panel--mobile': isMobileView }"
      >
        <slot name="color-menu" />
      </div>
    </transition>

    <!-- Backdrop for color menu -->
    <div
      v-if="colorMenuOpen"
      class="color-menu-button__backdrop"
      @click="closeColorMenu"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ColorMenuButton',

  props: {
    // Whether the current user is the artist viewing their own profile
    isMyProfile: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      colorMenuOpen: false
    }
  },

  computed: {
    ...mapState(['artist']),

    // Color menu visibility (artist on own profile only)
    showColorMenu() {
      return this.isMyProfile && this.artist?.artist
    }
  },

  methods: {
    toggleColorMenu() {
      this.colorMenuOpen = !this.colorMenuOpen
      this.$emit('color-menu-toggle', this.colorMenuOpen)
    },

    closeColorMenu() {
      this.colorMenuOpen = false
      this.$emit('color-menu-toggle', false)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "assets/sass/_variables";

.color-menu-button-wrapper {
  position: fixed;
  z-index: 18;
  bottom: 10px;
  left: 10px;

  &--open {
    z-index: 22;
  }
}

.color-menu-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  border: none;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: all 0.3s ease-out;
  color: rgba(255, 255, 255, 0.8);

  &:hover {
    background: rgba(0, 0, 0, 0.95);
    color: #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
    transform: translateY(-2px);
  }

  &--active {
    background: var(--primary-color, #fb3e52);
    color: #fff;

    &:hover {
      background: var(--primary-color, #fb3e52);
    }
  }

  // Mobile: pressed into bottom-left corner (mirrors footer-toggle on right)
  &--mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    width: auto;
    height: auto;
    padding: 10px 16px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom, 0));
    border-radius: 0 25px 0 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

    &:hover {
      background: rgba(0, 0, 0, 0.9);
      transform: none;
    }
  }

  &__icon {
    transition: transform 0.3s ease;

    .color-menu-button--active & {
      transform: rotate(45deg);
    }
  }

  &__panel {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 0;
    width: 300px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

    &--mobile {
      position: fixed;
      bottom: calc(48px + env(safe-area-inset-bottom, 0) + 10px);
      left: 10px;
      right: 10px;
      width: auto;
    }
  }

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }
}

// Slide up transition for color panel
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
